import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { Fancybox } from "@fancyapps/ui";
import "@fancyapps/ui/dist/fancybox/fancybox.css";
import moment from "moment";
import "moment/locale/ko";
import { emptyCheck, formatDateTime, maskMiddleCharacters } from "util/function/function";
import { getMatchingOne } from "firebase_/firebase_function/matching";
import { useUser } from "util/context/user_context";
import OnlyConfirmDialog from "components/onlyConfirm_dialog";
import OnlyLoadingPage from "components/loading_page";
import DetailPageButton from "components/repair/detail_page_button";
import {
  faChevronLeft,
  faClipboardCheck,
  faHandshake,
  faFlagCheckered,
  faMicrophone,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const C = {
  green: "#059669",
  greenDark: "#047857",
  greenBg: "#ECFDF5",
  greenBorder: "#A7F3D0",
  ink: "#111827",
  body: "#374151",
  sub: "#6B7280",
  line: "#E5E7EB",
  bg: "#F7FAF7",
  amber100: "#FEF3C7",
  amber800: "#92400E",
};

const STATUS_MAP = {
  0: { label: "매칭중", bg: C.amber100, fg: C.amber800, dot: "#F59E0B" },
  1: { label: "매칭완료", bg: "#DBEAFE", fg: "#1E40AF", dot: "#3B82F6" },
  2: { label: "완료", bg: C.greenBg, fg: C.greenDark, dot: C.green },
};

function StatusChip({ status }) {
  const s = STATUS_MAP[status] ?? STATUS_MAP[0];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "6px 14px",
        borderRadius: 9999,
        background: s.bg,
        color: s.fg,
        fontSize: 13,
        fontWeight: 700,
        whiteSpace: "nowrap",
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: 9999, background: s.dot }} />
      {s.label}
    </span>
  );
}

const categoryEmoji = (name = "") => {
  const s = String(name);
  if (s.includes("트랙터")) return "🚜";
  if (s.includes("콤바인")) return "🌾";
  if (s.includes("제어기") || s.includes("전자") || s.includes("전기")) return "⚡";
  if (s.includes("엔진") || s.includes("모터")) return "⚙️";
  if (s.includes("배터리")) return "🔋";
  if (s.includes("오일") || s.includes("연료")) return "🛢️";
  if (s.includes("이앙기") || s.includes("파종")) return "🌱";
  if (s.includes("바퀴") || s.includes("타이어")) return "⚫";
  return "🔧";
};

function Card({ children, padding = 24 }) {
  return (
    <section
      style={{
        background: "#fff",
        border: `1px solid ${C.line}`,
        borderRadius: 16,
        padding,
      }}
    >
      {children}
    </section>
  );
}

function CardTitle({ children, hint }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        gap: 12,
        marginBottom: 14,
      }}
    >
      <h3
        style={{
          fontSize: 17,
          fontWeight: 700,
          color: C.ink,
          letterSpacing: "-0.01em",
          margin: 0,
        }}
      >
        {children}
      </h3>
      {hint && (
        <span style={{ fontSize: 12, color: C.sub, fontWeight: 500 }}>{hint}</span>
      )}
    </div>
  );
}

function HeroCard({ repair }) {
  const names = repair?.categoryListName ?? [];
  const primary = names[0] || "기타";
  const secondary = names.slice(1).filter((n) => n && n !== "기타");
  const emoji = categoryEmoji(primary);
  const created = repair?.createDate;
  const expert = !emptyCheck(repair?.expertName) ? repair.expertName : null;

  return (
    <Card>
      <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
        <div
          style={{
            width: 88,
            height: 88,
            borderRadius: 16,
            background: C.greenBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 42,
            flexShrink: 0,
          }}
        >
          {emoji}
        </div>
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
            <h1
              style={{
                margin: 0,
                fontSize: 24,
                fontWeight: 800,
                color: C.ink,
                letterSpacing: "-0.02em",
                lineHeight: 1.25,
              }}
            >
              {primary}
              {secondary.length > 0 && (
                <span style={{ color: C.sub, fontWeight: 500 }}> · {secondary.join(" · ")}</span>
              )}
            </h1>
            <StatusChip status={repair?.status} />
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              fontSize: 13,
              color: C.sub,
              flexWrap: "wrap",
            }}
          >
            <span>요청일 {formatDateTime(created)}</span>
            <span style={{ width: 2, height: 2, background: "#D1D5DB", borderRadius: 9999 }} />
            <span>{created ? moment(created).locale("ko").fromNow() : ""}</span>
            {expert && (
              <>
                <span style={{ width: 2, height: 2, background: "#D1D5DB", borderRadius: 9999 }} />
                <span>{maskMiddleCharacters(expert)} 기사 배정</span>
              </>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

function DescriptionCard({ etc }) {
  const has = !!etc && String(etc).trim().length > 0;
  return (
    <Card>
      <CardTitle hint={has ? `${etc.length}자` : undefined}>기타 내용</CardTitle>
      {has ? (
        <p
          style={{
            margin: 0,
            fontSize: 15,
            lineHeight: 1.65,
            color: C.body,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {etc}
        </p>
      ) : (
        <div style={{ fontSize: 14, color: C.sub }}>기타 내용이 없습니다.</div>
      )}
    </Card>
  );
}

function VoiceCard({ voiceUrl }) {
  if (!voiceUrl) return null;
  return (
    <Card>
      <CardTitle>음성 메모</CardTitle>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          padding: "12px 14px",
          background: C.greenBg,
          border: `1px solid ${C.greenBorder}`,
          borderRadius: 12,
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 9999,
            background: C.greenDark,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <FontAwesomeIcon icon={faMicrophone} />
        </div>
        <audio
          controls
          src={voiceUrl}
          preload="metadata"
          style={{ flex: 1, minWidth: 0, height: 40 }}
        >
          이 브라우저는 오디오 재생을 지원하지 않습니다.
        </audio>
      </div>
    </Card>
  );
}

function PhotosCard({ images = [] }) {
  return (
    <Card>
      <CardTitle hint={images.length > 0 ? `${images.length}장` : undefined}>
        첨부된 사진
      </CardTitle>
      {images.length > 0 ? (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
            gap: 12,
          }}
        >
          {images.map((url, i) => (
            <a
              href={url}
              data-fancybox="gallery"
              data-caption={`${i + 1}/${images.length}`}
              key={i}
              style={{
                display: "block",
                aspectRatio: "1 / 1",
                borderRadius: 12,
                overflow: "hidden",
                border: `1px solid ${C.line}`,
                background: "#F3F4F6",
              }}
            >
              <img
                src={url}
                alt={`첨부 ${i + 1}`}
                style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
              />
            </a>
          ))}
        </div>
      ) : (
        <div style={{ fontSize: 14, color: C.sub }}>첨부된 사진이 없습니다.</div>
      )}
    </Card>
  );
}

function ProgressCard({ status }) {
  const steps = [
    { key: "0", label: "요청 등록", icon: faClipboardCheck },
    { key: "1", label: "매칭 완료", icon: faHandshake },
    { key: "2", label: "수리 완료", icon: faFlagCheckered },
  ];
  const currentIdx = steps.findIndex((s) => s.key === String(status));
  const reached = (i) => currentIdx >= i;

  return (
    <Card>
      <CardTitle>진행 상황</CardTitle>
      <div style={{ position: "relative", padding: "8px 12px" }}>
        {/* 라인 */}
        <div
          style={{
            position: "absolute",
            top: 32,
            left: 12 + 24,
            right: 12 + 24,
            height: 3,
            background: C.line,
            borderRadius: 9999,
          }}
        />
        <div
          style={{
            position: "absolute",
            top: 32,
            left: 12 + 24,
            width: `calc(${(currentIdx / (steps.length - 1)) * 100}% - ${(currentIdx / (steps.length - 1)) * 48}px)`,
            height: 3,
            background: C.green,
            borderRadius: 9999,
            transition: "width 0.4s ease",
          }}
        />
        <div style={{ display: "flex", justifyContent: "space-between", position: "relative" }}>
          {steps.map((step, i) => {
            const ok = reached(i);
            return (
              <div
                key={step.key}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 8,
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 9999,
                    background: ok ? C.greenDark : "#fff",
                    border: `2px solid ${ok ? C.greenDark : C.line}`,
                    color: ok ? "#fff" : C.sub,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.2s ease",
                    zIndex: 1,
                  }}
                >
                  <FontAwesomeIcon icon={step.icon} />
                </div>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: ok ? 700 : 500,
                    color: ok ? C.ink : C.sub,
                  }}
                >
                  {step.label}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
}

const MatchingDetailScreen = ({ isMobile }) => {
  const { docId } = useParams();
  const navigate = useNavigate();
  const { userData } = useUser();
  const [repair, setRepair] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notPageModal, setNotPageModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");

  useEffect(() => {
    setIsLoading(true);
    (async () => {
      const data = await getMatchingOne(docId);
      if (emptyCheck(data)) {
        setModalMessage("존재하지 않는 요청 입니다.");
        setNotPageModal(true);
        return;
      }
      setRepair(data);
      setIsLoading(false);
    })();
  }, [docId]);

  // Fancybox는 매 렌더마다 bind 호출되도 안전.
  Fancybox.bind("[data-fancybox]", {});

  const handleNotPage = () => {
    setNotPageModal(false);
    userData?.userType === "수리기사" ? navigate("/expertmain") : navigate("/main");
  };

  return (
    <>
      <div
        style={{
          fontFamily:
            'Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, sans-serif',
          background: C.bg,
          minHeight: "100vh",
          padding: "24px 20px 80px",
        }}
      >
        <div style={{ maxWidth: 900, margin: "0 auto", display: "flex", flexDirection: "column", gap: 16 }}>
          <button
            type="button"
            onClick={() => navigate(-1)}
            style={{
              alignSelf: "flex-start",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "8px 14px",
              borderRadius: 9999,
              background: "#fff",
              border: `1px solid ${C.line}`,
              color: C.body,
              fontSize: 13,
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
            <span>목록</span>
          </button>

          {isLoading || !repair ? (
            <div style={{ display: "flex", width: "100%", height: 500, alignItems: "center", justifyContent: "center" }}>
              <OnlyLoadingPage />
            </div>
          ) : (
            <>
              <HeroCard repair={repair} />
              <DescriptionCard etc={repair?.etc || ""} />
              <VoiceCard voiceUrl={repair?.voiceUrl} />
              <PhotosCard images={repair?.imageUrls || []} />
              <ProgressCard status={repair?.status} />

              {!isMobile && (
                <div
                  style={{
                    background: "#fff",
                    border: `1px solid ${C.line}`,
                    borderRadius: 16,
                    padding: 16,
                    position: "sticky",
                    bottom: 16,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
                  }}
                >
                  <DetailPageButton
                    userData={userData}
                    repair={repair}
                    docId={docId}
                    isMobile={isMobile}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {isMobile && repair && (
        <div
          style={{
            position: "sticky",
            bottom: 0,
            width: "100%",
            background: "#FFFFFF",
            padding: 16,
            borderTop: `1px solid ${C.line}`,
          }}
        >
          <DetailPageButton
            userData={userData}
            repair={repair}
            docId={docId}
            isMobile={isMobile}
          />
        </div>
      )}

      <OnlyConfirmDialog
        open={notPageModal}
        onClose={handleNotPage}
        message={modalMessage}
      />
    </>
  );
};

export default MatchingDetailScreen;
